/**
 * Le journal d'activité, équivalent du canal de combat de Dofus.
 *
 * Le moteur ne produit pas d'événements : il calcule un état. On déduit donc ce
 * qui vient de se passer **par différence** entre deux images, ce qui marche
 * indifféremment pour le combat et pour la récolte, sans rien ajouter au moteur.
 *
 * Deux points ont demandé du soin :
 *
 * - **un fil par personnage.** L'inventaire est commun au compte : déduire le
 *   butin de sa croissance affichait celui de tous les personnages dans le
 *   journal de celui qu'on regardait. Le moteur attribue donc le butin du pas à
 *   chacun (`p.dernierButin`), et chaque personnage a son propre fil.
 * - **des lignes qui s'agrègent.** Une ligne par objet et par pas donnait un mur
 *   de « +1 Blé » ; les gains consécutifs du même objet se cumulent maintenant
 *   dans une seule ligne.
 *
 * Rien n'est sauvegardé : c'est de l'affichage, pas de la progression.
 */
import type { Etat, Personnage } from "../sim/etat.ts"
import type { Contenu } from "../sim/contenu.ts"
import { item, monstre } from "../sim/contenu.ts"
import { slotDe } from "../sim/objets.ts"
import { esc, nb } from "./fmt.ts"
import { icone, cleClasse } from "./sprites.ts"

export type Ton = "kill" | "butin" | "xp" | "niveau" | "rare" | "perte"

export interface Ligne {
  ton: Ton
  texte: string
  /** identité de la ligne, pour cumuler deux gains du même objet */
  cle?: string
  n?: number
  /**
   * Rang d'apparition, commun à tous les fils.
   *
   * Chaque personnage a son fil, et un tableau ne dit pas dans quel ordre deux
   * fils se sont remplis. Ce compteur le dit, et permet de les entrelacer sans
   * horloge — une ligne qui se cumule reprend un rang neuf, puisqu'elle vient
   * de changer.
   */
  seq: number
  /** le personnage à qui la ligne appartient, dans le journal fusionné */
  qui?: string
}

const MAX = 40
/** Un butin sous ce taux mérite d'être signalé comme rare. */
const SEUIL_RARE = 5

interface Image {
  xp: number
  kills: number
  niveau: number
  cible: number | null
}

/** Un fil par personnage : `p.id` → ses lignes et son image précédente. */
const fils = new Map<string, { lignes: Ligne[]; avant: Image | null; xpDepuisKill: number }>()
let actif: string | null = null
let version = 0

let compteur = 0

const filDe = (id: string) => {
  let f = fils.get(id)
  if (!f) { f = { lignes: [], avant: null, xpDepuisKill: 0 }; fils.set(id, f) }
  return f
}

export const journal = (): Ligne[] => (actif ? filDe(actif).lignes : [])

/**
 * Les fils de toute l'équipe, entrelacés.
 *
 * L'écran Jeu montre les six personnages côte à côte, mais son journal n'en
 * racontait qu'un : « c'est un peu dérangeant, j'ai pas le journal de tous les
 * personnages en même temps ». On les mêle donc par rang d'apparition, chaque
 * ligne portant celui à qui elle appartient.
 */
export function journalEquipe(ids: readonly string[]): Ligne[] {
  const tout: Ligne[] = []
  for (const id of ids) {
    for (const l of filDe(id).lignes) tout.push({ ...l, qui: id })
  }
  return tout.sort((a, b) => b.seq - a.seq).slice(0, MAX)
}
export const versionJournal = () => version

/**
 * Ajoute une ligne, ou cumule dans une ligne existante du **groupe de butin
 * courant**, c'est-à-dire les lignes de butin qui précèdent la dernière victoire.
 *
 * Le cumul ne franchit pas une ligne de kill : « +3 Blé », « Bouftou vaincu »,
 * « +1 Blé » reste deux lignes, sinon le journal cesserait d'être chronologique.
 * À l'intérieur d'un même groupe en revanche, trois blés intercalés d'une griotte
 * n'ont aucune raison d'occuper trois lignes.
 */
const CUMULABLE: Ton[] = ["butin", "rare"]

function pousse(f: { lignes: Ligne[] }, ton: Ton, texte: string, cle?: string, n = 1) {
  if (cle) {
    const fin = f.lignes.findIndex((l) => !CUMULABLE.includes(l.ton))
    const borne = fin === -1 ? f.lignes.length : fin
    const i = f.lignes.findIndex((l, k) => k < borne && l.cle === cle)
    if (i !== -1) {
      const total = (f.lignes[i]!.n ?? 1) + n
      const maj = [...f.lignes]
      maj[i] = { ...maj[i]!, n: total, seq: ++compteur, texte: texte.replace(/^\+\d+/, `+${nb(total)}`) }
      f.lignes = maj
      version++
      return
    }
  }
  f.lignes = [{ ton, texte, cle, n, seq: ++compteur }, ...f.lignes].slice(0, MAX)
  version++
}

/** Taux de drop du butin sur la cible courante, pour repérer les raretés. */
function tauxSur(c: Contenu, cibleId: number | null, itemId: number): number {
  const m = cibleId !== null ? monstre(c, cibleId) : null
  const b = m?.butin.find((x) => x.item === itemId)
  return b ? Math.max(...b.taux) : 100
}

/**
 * Compare l'état à l'image précédente et consigne ce qui a changé.
 * À appeler à chaque rafraîchissement ; ne fait rien si rien n'a bougé.
 */
/**
 * Consigne **toute l'équipe**, et retient qui est l'actif.
 *
 * Seul le personnage affiché était consigné : les fils des autres restaient
 * vides, si bien qu'un journal d'équipe n'aurait rien eu à montrer. Le moteur
 * attribue pourtant le butin de chaque pas à son personnage depuis longtemps.
 */
export function consignerEquipe(etat: Etat, c: Contenu) {
  const id = etat.persoActif
  if (actif !== id) { actif = id ?? null; version++ }
  for (const p of etat.personnages ?? []) consigner(etat, p, c, true)
}

export function consigner(etat: Etat, p: Personnage | undefined, c: Contenu, garderActif = false) {
  if (!p) { if (!garderActif) actif = null; return }
  if (!garderActif && actif !== p.id) { actif = p.id; version++ }
  const f = filDe(p.id)

  const inst: Image = { xp: p.xp, kills: p.kills, niveau: p.niveau, cible: p.cible }
  if (!f.avant) { f.avant = inst; return }

  // le butin, avant le kill : on veut lire « +3 Laine » sous « Bouftou vaincu »
  for (const [id, gain] of Object.entries(p.dernierButin ?? {})) {
    const it = item(c, Number(id))
    if (!it || gain <= 0) continue
    // Une pièce d'équipement ne s'empile pas : elle part au coffre avec son jet.
    if (slotDe(it)) {
      pousse(f, "rare", `★ ${esc(it.nom)}, pièce trouvée`)
      continue
    }
    const rare = tauxSur(c, f.avant.cible, Number(id)) < SEUIL_RARE
    pousse(f, rare ? "rare" : "butin",
      `${rare ? "★ " : ""}+${nb(gain)} ${esc(it.nom)}`, `b${id}`, gain)
  }

  f.xpDepuisKill += Math.max(0, inst.xp - f.avant.xp)

  if (inst.kills > f.avant.kills) {
    const nom = monstre(c, f.avant.cible ?? -1)?.nom ?? "Monstre"
    const tours = inst.kills - f.avant.kills
    pousse(f, "kill", `${esc(nom)} vaincu${tours > 1 ? ` ×${tours}` : ""} · +${nb(f.xpDepuisKill)} XP`)
    f.xpDepuisKill = 0
  }

  if (inst.niveau > f.avant.niveau) {
    pousse(f, "niveau", `Niveau ${inst.niveau} atteint`)
    f.xpDepuisKill = 0
  }

  if (p.arrete && !f.lignes.some((l) => l.ton === "perte")) {
    pousse(f, "perte", "Ton personnage est tombé, le farm s'arrête")
  }

  f.avant = inst
}

/** Vide le journal du personnage actif, au changement d'activité. */
export function viderJournal() {
  if (actif) fils.set(actif, { lignes: [], avant: null, xpDepuisKill: 0 })
  version++
}

export function rendreJournal(): string {
  const lignes = journal()
  if (lignes.length === 0) {
    return `<p class="aide">Le journal se remplira dès le premier coup porté.</p>`
  }
  return lignes.map((l) => `<div class="jl ${l.ton}">${l.texte}</div>`).join("")
}

/**
 * Le journal de l'équipe : les mêmes lignes, précédées de qui les a écrites.
 *
 * La tête de classe plutôt que le nom seul : six lignes qui commencent par un
 * prénom se ressemblent toutes, six têtes se distinguent d'un coup d'œil.
 */
export function rendreJournalEquipe(
  equipe: readonly { id: string; nom: string; classe: number }[],
): string {
  const lignes = journalEquipe(equipe.map((x) => x.id))
  if (lignes.length === 0) {
    return `<p class="aide">Le journal se remplira dès le premier coup porté.</p>`
  }
  const parId = new Map(equipe.map((x) => [x.id, x]))
  return lignes.map((l) => {
    const x = l.qui ? parId.get(l.qui) : undefined
    return `<div class="jl ${l.ton}">${x
      ? `<span class="qui" style="${icone("classes", cleClasse(x.classe), 18)}" title="${esc(x.nom)}"></span>`
      : ""}${l.texte}</div>`
  }).join("")
}
